import { ArrowDown, ArrowRight, ArrowUp } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { cn } from "@/lib/utils";

import type { DiscountAnalyticsReportShape } from "../../types/discount-analytics-types";
import { LockedChartCard } from "../locked-chart-card";
import { CampaignDetailMock, SummaryMock, TrendChartMock } from "./discount-mock-content";
import { LifetimeValueCard } from "./lifetime-value-card";
import { TimingHeatmap } from "./timing-heatmap";

interface Props {
  data: DiscountAnalyticsReportShape;
  comparison: DiscountAnalyticsReportShape | null;
  isPremium: boolean;
}

function DeltaBadge({
  current,
  previous,
  isPercentageMetric,
}: {
  current: number;
  previous: number | undefined;
  isPercentageMetric?: boolean;
}) {
  if (previous == null) return null;

  // For percentage metrics (rate-style: 0-1 floats), delta is absolute
  // percentage-point difference. For absolute numbers, delta is a relative
  // percentage growth.
  let displayDelta: string;
  let positive = false;
  let neutral = false;

  if (isPercentageMetric) {
    const diff = (current - previous) * 100;
    if (Math.abs(diff) < 0.05) {
      neutral = true;
      displayDelta = "0pp";
    } else {
      positive = diff > 0;
      displayDelta = `${diff > 0 ? "+" : ""}${diff.toFixed(1)}pp`;
    }
  } else {
    if (previous === 0) {
      if (current === 0) {
        neutral = true;
        displayDelta = "0%";
      } else {
        positive = true;
        displayDelta = "+∞";
      }
    } else {
      const pct = ((current - previous) / Math.abs(previous)) * 100;
      if (Math.abs(pct) < 0.05) {
        neutral = true;
        displayDelta = "0%";
      } else {
        positive = pct > 0;
        displayDelta = `${pct > 0 ? "+" : ""}${pct.toFixed(0)}%`;
      }
    }
  }

  const Icon = neutral ? ArrowRight : positive ? ArrowUp : ArrowDown;
  const colorClass = neutral
    ? "bg-slate-100 text-slate-700 border-slate-200"
    : positive
      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
      : "bg-rose-100 text-rose-800 border-rose-200";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] font-bold tabular-nums",
        colorClass
      )}
    >
      <Icon className="h-2.5 w-2.5" />
      {displayDelta}
    </span>
  );
}

function MetricCard({
  label,
  children,
  delta,
}: {
  label: string;
  children: React.ReactNode;
  delta?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="text-xl font-semibold space-y-1">
        <div>{children}</div>
        {delta ?? null}
      </CardContent>
    </Card>
  );
}

function formatPercent(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

function formatNumber(v: number): string {
  return new Intl.NumberFormat().format(v);
}

export function DiscountSummary({ data, comparison, isPremium }: Props) {
  const { t } = useTranslation("reports");
  const s = data.summary;
  const c = comparison?.summary;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <MetricCard
          label={t("discounts.summary.totalAmountDiscounted")}
          delta={
            <DeltaBadge current={s.totalAmountDiscounted} previous={c?.totalAmountDiscounted} />
          }
        >
          <PriceDisplay amount={s.totalAmountDiscounted} />
        </MetricCard>
        <MetricCard
          label={t("discounts.summary.totalDiscountedRevenue")}
          delta={
            <DeltaBadge current={s.totalDiscountedRevenue} previous={c?.totalDiscountedRevenue} />
          }
        >
          <PriceDisplay amount={s.totalDiscountedRevenue} />
        </MetricCard>
        <MetricCard
          label={t("discounts.summary.redemptionCount")}
          delta={
            <DeltaBadge
              current={s.totalDiscountedAppointments}
              previous={c?.totalDiscountedAppointments}
            />
          }
        >
          {formatNumber(s.totalDiscountedAppointments)}
        </MetricCard>
        <MetricCard
          label={t("discounts.summary.uniqueCustomers")}
          delta={
            <DeltaBadge
              current={s.uniqueCustomersUsingDiscount}
              previous={c?.uniqueCustomersUsingDiscount}
            />
          }
        >
          {formatNumber(s.uniqueCustomersUsingDiscount)}
        </MetricCard>
        <MetricCard
          label={t("discounts.summary.discountedAppointmentRate")}
          delta={
            <DeltaBadge
              current={s.discountedAppointmentRate}
              previous={c?.discountedAppointmentRate}
              isPercentageMetric
            />
          }
        >
          {formatPercent(s.discountedAppointmentRate)}
        </MetricCard>
        <MetricCard
          label={t("discounts.summary.newCustomerShare")}
          delta={
            <DeltaBadge
              current={s.newCustomerShare}
              previous={c?.newCustomerShare}
              isPercentageMetric
            />
          }
        >
          {formatPercent(s.newCustomerShare)}
        </MetricCard>
        <MetricCard
          label={t("discounts.summary.averageOrderValueDiscounted")}
          delta={
            <DeltaBadge
              current={s.averageOrderValueDiscounted}
              previous={c?.averageOrderValueDiscounted}
            />
          }
        >
          <PriceDisplay amount={Math.round(s.averageOrderValueDiscounted)} />
        </MetricCard>
        <MetricCard
          label={t("discounts.summary.averageOrderValueNonDiscounted")}
          delta={
            <DeltaBadge
              current={s.averageOrderValueNonDiscounted}
              previous={c?.averageOrderValueNonDiscounted}
            />
          }
        >
          <PriceDisplay amount={Math.round(s.averageOrderValueNonDiscounted)} />
        </MetricCard>
      </div>

      {isPremium ? (
        <LifetimeValueCard summary={s} />
      ) : (
        <LockedChartCard
          title={t("discounts.lifetimeValue.title")}
          mockContent={<SummaryMock />}
          height="auto"
        />
      )}

      {isPremium ? (
        <TimingHeatmap dayOfWeek={s.dayOfWeekDistribution} hourOfDay={s.hourOfDayDistribution} />
      ) : (
        <LockedChartCard
          title={t("discounts.timing.title")}
          mockContent={<TrendChartMock />}
          height="auto"
        />
      )}

      {!isPremium && (
        <LockedChartCard
          title={t("discounts.summary.wasted.title")}
          mockContent={<CampaignDetailMock />}
          height="auto"
        />
      )}

      {isPremium && (
        <div className="grid gap-3 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium">
                {t("discounts.summary.wasted.title")}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t("discounts.summary.wasted.tooltip")}
              </p>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row
                label={t("discounts.summary.wasted.totalCount")}
                value={formatNumber(s.wastedRedemptions.totalCount)}
                extra={<PriceDisplay amount={s.wastedRedemptions.totalAmountDiscounted} />}
              />
              <Row
                label={t("discounts.summary.wasted.declined")}
                value={formatNumber(s.wastedRedemptions.declinedCount)}
                extra={<PriceDisplay amount={s.wastedRedemptions.declinedAmount} />}
              />
              <Row
                label={t("discounts.summary.wasted.noShow")}
                value={formatNumber(s.wastedRedemptions.noShowCount)}
                extra={<PriceDisplay amount={s.wastedRedemptions.noShowAmount} />}
              />
              <Row
                label={t("discounts.summary.wasted.rescheduled")}
                value={formatNumber(s.wastedRedemptions.rescheduledCount)}
                extra={<PriceDisplay amount={s.wastedRedemptions.rescheduledAmount} />}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium">
                {t("discounts.summary.inFlight.title")}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t("discounts.summary.inFlight.tooltip")}
              </p>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row
                label={t("discounts.summary.inFlight.totalCount")}
                value={formatNumber(s.inFlightRedemptions.totalCount)}
              />
              <Row
                label={t("discounts.summary.inFlight.totalAmount")}
                value=""
                extra={<PriceDisplay amount={s.inFlightRedemptions.totalAmountDiscounted} />}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, extra }: { label: string; value: string; extra?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">
        {value}
        {extra ? <span className="ml-2 text-muted-foreground">{extra}</span> : null}
      </span>
    </div>
  );
}
